/**
 * RoleLadderEditor — the Roles section of the equipment detail page (#333).
 *
 * A Role is a requirement; the ladder is the ordered chain of Equipment that
 * can satisfy it. This edits, for ONE item, which Roles it belongs to and where
 * it sits in each Role's ladder — showing the other members either side rather
 * than an abstract "#3 of 3".
 *
 * Extracted verbatim in mechanics from the old EquipmentEditForm, which
 * rendered it inside a quarter-width grid cell. Here it spans the page.
 *
 * Commit model (settings rows, DESIGN.md): every assign / remove / reorder
 * commits on its own. `chain_assignments` is a FULL REPLACEMENT of this item's
 * memberships, so each commit sends the complete assigned set; `role_orders`
 * carries only the roles whose OTHER members were actually reordered, and may
 * never travel without chain_assignments (the Go handler 400s).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useDismissable } from "../../lib/use-dismissable";
import { useListboxNav } from "../../lib/use-listbox-nav";
import { ConfirmModal } from "../ui/ConfirmModal";
import { Tooltip } from "../ui/Tooltip";
import { cx } from "@shared/cx";
import forms from "../ui/field.module.css";
import srOnly from "@shared/a11y.module.css";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "../ui/icons";
import {
  roleId,
  type ChainAssignment,
  type GearRole,
  type RoleOrderEntry,
} from "../../lib/equipment-api";
import styles from "./RoleLadderEditor.module.css";

interface RoleSlot {
  assigned: boolean;
  sortOrder: number;
}

/** The local, uncommitted picture of this item's place in every ladder. */
interface Draft {
  /** roleId(role) → {assigned, sortOrder}. */
  slots: Record<string, RoleSlot>;
  /** roleId(role) → the OTHER members' equipment ids in display order. */
  otherOrder: Record<string, string[]>;
  /** Roles whose other-member order the operator actually changed. */
  touched: Set<string>;
}

interface RoleLadderEditorProps {
  itemId: string;
  itemName: string;
  gearRoles: GearRole[];
  /** Commits the complete membership set; must throw on failure. */
  onCommit: (payload: {
    chain_assignments: ChainAssignment[];
    role_orders: RoleOrderEntry[];
  }) => Promise<void>;
  /**
   * Creates a role named by the typed text and assigns this item to it
   * (#320); must throw on failure. Absent = no "Create" row in the picker
   * (multi-profile companies, where a bare name can't say which profile).
   */
  onCreateRole?: (displayName: string) => Promise<void>;
  /** Renames a role (display name only); must throw on failure. */
  onRenameRole?: (role: GearRole, displayName: string) => Promise<void>;
  /** Deletes an EMPTY role — the server re-guards; must throw on failure. */
  onDeleteRole?: (role: GearRole) => Promise<void>;
}

/** Seed the draft from the server's ladders. */
function seed(gearRoles: GearRole[], itemId: string): Draft {
  const slots: Record<string, RoleSlot> = {};
  const otherOrder: Record<string, string[]> = {};
  for (const role of gearRoles) {
    const idx = role.members.findIndex((m) => m.equipment_id === itemId);
    slots[roleId(role)] = {
      assigned: idx >= 0,
      sortOrder: idx >= 0 ? role.members[idx].sort_order : role.members.length,
    };
    otherOrder[roleId(role)] = role.members
      .filter((mem) => mem.equipment_id !== itemId)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((mem) => mem.equipment_id);
  }
  return { slots, otherOrder, touched: new Set() };
}

export function RoleLadderEditor({
  itemId,
  itemName,
  gearRoles,
  onCommit,
  onCreateRole,
  onRenameRole,
  onDeleteRole,
}: RoleLadderEditorProps) {
  const [draft, setDraft] = useState<Draft>(() => seed(gearRoles, itemId));
  const [roleSearch, setRoleSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Role management in the picker (#320): rename edits in place on the row;
  // delete confirms first and only ever offers itself on an EMPTY role.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<GearRole | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reseed whenever the server's ladders change — i.e. after each commit's
  // refetch, which is also what clears `touched`.
  useEffect(() => {
    setDraft(seed(gearRoles, itemId));
  }, [gearRoles, itemId]);

  useDismissable({
    open: menuOpen,
    onDismiss: () => setMenuOpen(false),
    pointer: [dropdownRef],
  });

  // Profile labels only matter when the Company runs more than one enabled
  // profile — single-profile tenants read exactly as before.
  const multiProfile = useMemo(
    () => new Set(gearRoles.map((r) => r.profile_key)).size > 1,
    [gearRoles],
  );
  const roleLabel = (r: GearRole): string =>
    multiProfile ? `${r.display_name} · ${r.profile_label}` : r.display_name;

  /** Other members of a role (this item excluded) — the bottom-of-ladder index. */
  const otherCount = (role: GearRole): number =>
    role.members.filter((mem) => mem.equipment_id !== itemId).length;

  /** Set the draft AND commit it. Reverts to the server picture on failure. */
  const apply = async (next: Draft): Promise<void> => {
    const previous = draft;
    setDraft(next);
    setSaving(true);
    try {
      await onCommit(payloadFor(next));
      setError(null);
    } catch (err) {
      setDraft(previous);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  /** The COMPLETE membership set + only the touched roles' member orders. */
  const payloadFor = (
    d: Draft,
  ): {
    chain_assignments: ChainAssignment[];
    role_orders: RoleOrderEntry[];
  } => {
    const chain_assignments: ChainAssignment[] = [];
    const role_orders: RoleOrderEntry[] = [];
    // Iterate gearRoles (not the slots record) so profile_key/role_key come off
    // the role object rather than being parsed back out of the composite key.
    for (const r of gearRoles) {
      const slot = d.slots[roleId(r)];
      if (!slot?.assigned) continue;
      chain_assignments.push({
        profile_key: r.profile_key,
        role_key: r.role_key,
        sort_order: Number.isFinite(slot.sortOrder)
          ? Math.max(0, slot.sortOrder)
          : 0,
      });
      const order = d.otherOrder[roleId(r)];
      if (d.touched.has(roleId(r)) && order) {
        role_orders.push({
          profile_key: r.profile_key,
          role_key: r.role_key,
          equipment_ids: order,
        });
      }
    }
    return { chain_assignments, role_orders };
  };

  const assignRole = (role: GearRole): void => {
    setRoleSearch("");
    setMenuOpen(false);
    void apply({
      ...draft,
      slots: {
        ...draft.slots,
        [roleId(role)]: { assigned: true, sortOrder: otherCount(role) },
      },
    });
  };

  const removeRole = (role: GearRole): void => {
    void apply({
      ...draft,
      slots: {
        ...draft.slots,
        [roleId(role)]: { ...draft.slots[roleId(role)], assigned: false },
      },
    });
  };

  /** Shift this item's own position within a role's ladder. */
  const withMovedItem = (role: GearRole, delta: number): Draft | null => {
    const cur = draft.slots[roleId(role)];
    if (!cur) return null;
    const max = otherCount(role);
    const next = Math.min(max, Math.max(0, cur.sortOrder + delta));
    if (next === cur.sortOrder) return null;
    return {
      ...draft,
      slots: { ...draft.slots, [roleId(role)]: { ...cur, sortOrder: next } },
    };
  };

  const assignedRoles = useMemo(
    () =>
      gearRoles
        .filter((r) => draft.slots[roleId(r)]?.assigned)
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [gearRoles, draft],
  );

  /**
   * The full preference ladder for a role, with THIS item spliced in at its
   * current position. The other members come from the (reorderable) otherOrder
   * state; `isThis` marks this item's row.
   */
  const ladderFor = (
    role: GearRole,
  ): Array<{ id: string; name: string; isThis: boolean }> => {
    const nameById = new Map(
      role.members.map((mem) => [mem.equipment_id, mem.name]),
    );
    const orderedIds = draft.otherOrder[roleId(role)] ?? [];
    const others = orderedIds.map((id) => ({
      id,
      name: nameById.get(id) ?? "",
      isThis: false,
    }));
    const pos = Math.min(
      others.length,
      Math.max(0, draft.slots[roleId(role)]?.sortOrder ?? others.length),
    );
    const list = [...others];
    list.splice(pos, 0, {
      id: itemId,
      name: itemName.trim() || "This item",
      isThis: true,
    });
    return list;
  };

  /**
   * Move one ladder row one step — every row carries its own up/down controls.
   * Three cases keep the single ordered list coherent:
   *   - this item's row → its slot sortOrder shifts (others untouched);
   *   - another row swaps places with this item → only this item's position
   *     changes (the others' relative order is preserved, so the role is NOT
   *     marked touched and no role_orders entry is sent);
   *   - two other members swap → their order changes and the role is marked
   *     touched so the payload carries its new role_orders entry.
   */
  const moveRow = (role: GearRole, i: number, delta: number): void => {
    const ladder = ladderFor(role);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ladder.length) return;
    const sel = ladder[i];
    const neighbor = ladder[j];
    if (sel.isThis) {
      const next = withMovedItem(role, delta);
      if (next) void apply(next);
      return;
    }
    if (neighbor.isThis) {
      // Swapping with this item: the selected row keeps its place among the
      // OTHERS; this item's slot moves the opposite way past it.
      const next = withMovedItem(role, -delta);
      if (next) void apply(next);
      return;
    }
    const cur = draft.otherOrder[roleId(role)] ?? [];
    const a = cur.indexOf(sel.id);
    const b = cur.indexOf(neighbor.id);
    if (a < 0 || b < 0) return;
    const reordered = [...cur];
    [reordered[a], reordered[b]] = [reordered[b], reordered[a]];
    void apply({
      ...draft,
      otherOrder: { ...draft.otherOrder, [roleId(role)]: reordered },
      touched: new Set(draft.touched).add(roleId(role)),
    });
  };

  // Browse hits: every unassigned role, filtered by the query when one is typed.
  // Empty query shows the whole catalogue (the operator can't memorise 28
  // names), so this is a browsable dropdown, not a recall-only search.
  const roleHits = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    return gearRoles
      .filter(
        (r) =>
          !draft.slots[roleId(r)]?.assigned &&
          (q === "" || r.display_name.toLowerCase().includes(q)),
      )
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [gearRoles, draft, roleSearch]);

  // "Create '<typed>'" appends to the hit list when the typed text names no
  // existing role — the pick-or-create combobox pattern, so a role can be
  // born while tagging the first item that backs it (#320).
  const typed = roleSearch.trim();
  const canCreate =
    onCreateRole != null &&
    typed !== "" &&
    !gearRoles.some(
      (r) => r.display_name.toLowerCase() === typed.toLowerCase(),
    );

  const createRole = async (): Promise<void> => {
    if (!onCreateRole || typed === "") return;
    setMenuOpen(false);
    setRoleSearch("");
    setSaving(true);
    try {
      await onCreateRole(typed);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const commitRename = async (role: GearRole): Promise<void> => {
    const name = renameDraft.trim();
    setRenaming(null);
    if (!onRenameRole || name === "" || name === role.display_name) return;
    setSaving(true);
    try {
      await onRenameRole(role, name);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!onDeleteRole || !deleteTarget) return;
    setDeleting(true);
    try {
      await onDeleteRole(deleteTarget);
      setDeleteTarget(null);
      setDeleteError(null);
    } catch (err) {
      // Stays in the modal, beside the role it belongs to — the server's
      // demand-rule guard can still refuse an empty role.
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const optionCount = roleHits.length + (canCreate ? 1 : 0);
  const menuNav = useListboxNav({
    open: menuOpen && optionCount > 0,
    count: optionCount,
    onCommit: (i) =>
      i < roleHits.length ? assignRole(roleHits[i]) : void createRole(),
  });

  return (
    <div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}

      {/* Full column width, like every other block in the space — the picker
          and the ladders it feeds share one left and right edge. */}
      <div className={styles.picker} ref={dropdownRef}>
        <label htmlFor="role-search" className={srOnly.srOnly}>
          Search gear roles
        </label>
        <div className={styles.searchField}>
          <PlusIcon className={styles.searchIcon} />
          <input
            id="role-search"
            type="text"
            value={roleSearch}
            onChange={(e) => {
              setRoleSearch(e.target.value);
              setMenuOpen(true);
            }}
            onFocus={() => setMenuOpen(true)}
            onKeyDown={menuNav.onKeyDown}
            {...menuNav.comboboxProps}
            placeholder="Search or browse roles..."
            aria-label="Search gear roles"
            className={cx(forms.input, styles.searchInput)}
          />
        </div>
        {menuOpen && (
          <div
            id={menuNav.listboxId}
            role="listbox"
            aria-label="Gear role suggestions"
            className={styles.menu}
          >
            {roleHits.map((role, i) =>
              renaming === roleId(role) ? (
                <div key={roleId(role)} className={styles.renameRow}>
                  <input
                    type="text"
                    value={renameDraft}
                    autoFocus
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={() => void commitRename(role)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        setRenaming(null);
                      }
                    }}
                    aria-label={`Rename ${roleLabel(role)}`}
                    className={styles.renameInput}
                  />
                </div>
              ) : (
                <div
                  key={roleId(role)}
                  className={cx(
                    styles.option,
                    i === menuNav.activeIndex && styles.optionActive,
                  )}
                >
                  <button
                    id={menuNav.optionId(i)}
                    type="button"
                    role="option"
                    aria-selected={i === menuNav.activeIndex}
                    tabIndex={-1}
                    onClick={() => assignRole(role)}
                    aria-label={`Assign to ${roleLabel(role)}`}
                    className={styles.optionLabel}
                  >
                    {roleLabel(role)}
                  </button>
                  {onRenameRole && (
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => {
                        setRenaming(roleId(role));
                        setRenameDraft(role.display_name);
                      }}
                      aria-label={`Rename ${roleLabel(role)}`}
                      className={styles.rowAction}
                    >
                      <PencilIcon className={styles.icon} />
                    </button>
                  )}
                  {onDeleteRole &&
                    (role.members.length === 0 ? (
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteTarget(role);
                        }}
                        aria-label={`Delete ${roleLabel(role)}`}
                        className={styles.rowAction}
                      >
                        <TrashIcon className={styles.icon} />
                      </button>
                    ) : (
                      // Not deletable while equipment backs it — the tooltip
                      // says why instead of a dead or missing control.
                      <Tooltip
                        content="Remove its equipment from the role first — a role with gear assigned can't be deleted."
                        className={styles.rowActionDisabled}
                      >
                        <TrashIcon className={styles.icon} />
                      </Tooltip>
                    ))}
                </div>
              ),
            )}
            {canCreate && (
              <button
                id={menuNav.optionId(roleHits.length)}
                type="button"
                role="option"
                aria-selected={roleHits.length === menuNav.activeIndex}
                tabIndex={-1}
                onClick={() => void createRole()}
                className={cx(
                  styles.createOption,
                  roleHits.length === menuNav.activeIndex && styles.optionActive,
                )}
              >
                Create &lsquo;{typed}&rsquo;
              </button>
            )}
            {optionCount === 0 && (
              <p className={styles.empty}>
                {roleSearch.trim() ? "No matching roles" : "All roles assigned"}
              </p>
            )}
          </div>
        )}
      </div>

      {assignedRoles.length === 0 ? (
        <p className={styles.noRoles}>
          Not in any role yet. The picker can only reach this item through a
          Role, or by being named directly on a Booking.
        </p>
      ) : (
        // Unboxed, like every settings list: each role is a small heading over
        // hairline-divided ladder rows, running the same width as the picker
        // above. Every row carries its own move controls — nothing to select
        // first.
        <div className={cx(styles.ladders, saving && styles.saving)}>
          {assignedRoles.map((role) => {
            const ladder = ladderFor(role);
            return (
              <section
                key={roleId(role)}
                aria-label={`${roleLabel(role)} ladder`}
              >
                <div className={styles.roleHead}>
                  <Tooltip content="The order the picker tries this Role's Equipment when stock runs short.">
                    <span className={styles.roleName}>
                      {roleLabel(role)}
                    </span>
                  </Tooltip>
                  <button
                    type="button"
                    onClick={() => removeRole(role)}
                    disabled={saving}
                    aria-label={`Remove from ${roleLabel(role)}`}
                    className={styles.removeRole}
                  >
                    <XIcon className={styles.icon} />
                  </button>
                </div>
                <ol className={styles.ladder}>
                  {ladder.map((entry, i) => (
                    <li key={entry.id} className={styles.rung}>
                      <span className={styles.rungIndex}>
                        {i + 1}.
                      </span>
                      <span
                        className={cx(
                          styles.rungName,
                          entry.isThis && styles.rungNameThis,
                        )}
                      >
                        {entry.name}
                        {entry.isThis && (
                          <span className={styles.thisItem}>
                            this item
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => moveRow(role, i, -1)}
                        disabled={saving || i === 0}
                        aria-label={`Move ${entry.name} up in ${roleLabel(role)}`}
                        className={styles.move}
                      >
                        <ChevronUpIcon className={styles.icon} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveRow(role, i, 1)}
                        disabled={saving || i === ladder.length - 1}
                        aria-label={`Move ${entry.name} down in ${roleLabel(role)}`}
                        className={styles.move}
                      >
                        <ChevronDownIcon className={styles.icon} />
                      </button>
                    </li>
                  ))}
                </ol>
              </section>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={deleteTarget != null}
        title={`Delete role "${deleteTarget?.display_name ?? ""}"`}
        message="This cannot be undone."
        error={deleteError}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
